import { apolloClient } from '@/apollo';
import {
  CreateOrUpdateCodeInput,
  CreateOrUpdateQuotationInput,
  CreateOrUpdateQuotationsDocument,
  CreateOrUpdateQuotationsMutation,
  CreateOrUpdateQuotationsMutationVariables,
  Project,
  ProjectDocument,
  ProjectQuery,
  ProjectQueryVariables,
} from '@/graphql/operations.gen';
import { map, pick } from 'ramda';
import { computeCodeOccurrences, quotationSourcesReactive } from '@/apollo/cache';
import { pickCodeInput } from '@/store/modules/aicoding';

const pickQuotationInput = pick<'id' | 'name' | 'comment' | 'isNew' | 'sourceName' | 'sourceId' | 'from' | 'to'>([
  'id',
  'name',
  'comment',
  'isNew',
  'sourceName',
  'sourceId',
  'from',
  'to',
]);

export const createOrUpdateQuotationsOperation = (data: {
  codes: Array<CreateOrUpdateCodeInput>;
  parentCodes: Array<CreateOrUpdateCodeInput>;
  quotations: Array<CreateOrUpdateQuotationInput>;
  projectId: string;
}) =>
  apolloClient.mutate<CreateOrUpdateQuotationsMutation, CreateOrUpdateQuotationsMutationVariables>({
    mutation: CreateOrUpdateQuotationsDocument,
    variables: {
      projectId: data.projectId,
      quotations: data.quotations.map((quotation) => ({
        ...pickQuotationInput(quotation),
        codes: quotation.codes.map((code) => pickCodeInput(code)),
      })),
      codes: data.codes.map((code) => ({
        ...pickCodeInput(code),
        parent: code.parent ? pickCodeInput(code.parent) : null,
      })),
      parentCodes: data.parentCodes.map((code) => ({
        ...pickCodeInput(code),
        parent: code.parent ? pickCodeInput(code.parent) : null,
      })),
    },
    update(cache, { data: { createOrUpdateQuotations } }) {
      const { project } = cache.readQuery<{ project: Project }>({
        query: ProjectDocument,
        variables: { id: data.projectId },
      });

      // we need to update the reactive quotation source store here to prvent Project refetch (I think)
      const quotationSourceIds = new Set<string>(
        map((quotation) => quotation.sourceId, createOrUpdateQuotations.quotations)
      );

      const quotationSources = quotationSourcesReactive();

      createOrUpdateQuotations.quotations.forEach((quotation) => {
        quotationSources.set(quotation.id, quotation.sourceId);
      });

      quotationSourcesReactive(new Map(quotationSources));

      const updatedSources = map((source) => {
        if (quotationSourceIds.has(source.id)) {
          const quotationsForSource = createOrUpdateQuotations.quotations.filter(
            (quotation) => quotation.sourceId === source.id
          );

          return {
            ...source,
            quotations: [
              // filter quotations that have been updated by ai coding
              ...source.quotations.filter((sourceQuotation) =>
                quotationsForSource.find((quotationForSource) => quotationForSource.id !== sourceQuotation.id)
              ),
              ...quotationsForSource,
            ],
          };
        }

        return source;
      }, project.sources);

      cache.writeQuery({
        query: ProjectDocument,
        data: {
          project: {
            ...project,
            sources: [...updatedSources],
            codes: [...project.codes, ...createOrUpdateQuotations.parentCodes, ...createOrUpdateQuotations.codes],
          },
        },
        variables: {
          id: data.projectId,
        },
      });

      computeCodeOccurrences(
        cache.readQuery<ProjectQuery, ProjectQueryVariables>({
          query: ProjectDocument,
          variables: { id: data.projectId },
        }).project
      );
    },
  });
